import * as React from 'react';
import { Tooltip as TooltipPrimitive } from 'radix-ui';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

type RootProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>;

const TooltipContext = React.createContext<((v: boolean | ((p: boolean) => boolean)) => void) | null>(null);

const Tooltip = ({ children, ...props }: RootProps) => {
  const [open, setOpen] = React.useState(false);

  return (
    <TooltipContext.Provider value={setOpen}>
      <TooltipPrimitive.Root {...props} open={open} onOpenChange={setOpen}>
        {children}
      </TooltipPrimitive.Root>
    </TooltipContext.Provider>
  );
};

type TriggerProps = React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>;

const TooltipTrigger = React.forwardRef<React.ElementRef<typeof TooltipPrimitive.Trigger>, TriggerProps>(
  ({ onClick, ...props }, ref) => {
    const setOpen = React.useContext(TooltipContext);

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (e) => {
      setOpen?.(true);
      onClick?.(e);
    };

    return <TooltipPrimitive.Trigger ref={ref} onClick={handleClick} {...props} />;
  },
);

TooltipTrigger.displayName = 'TooltipTrigger';

const TooltipPortal = TooltipPrimitive.Portal;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md bg-input-background px-2 py-1 text-xs text-black animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 dark:text-white',
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipPortal, TooltipContent, TooltipProvider };
